import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { getStyles } from '../../../theme/styles';
import { InputLabel, MenuItem, Select, FormControl } from '@mui/material';
import courseService from '../../../services/CourseService';
import { CourseSetDTO } from '@beable/roster-backend-client-v1';

interface CourseSetSelectorProps {
  courseSetId?: string;
  locationCode?: string;
  siteId?: number;
  onChange: any;
  disabled?: boolean;
  variant?: 'filled' | 'outlined';
  refresh?: boolean;
  className?: string;
  inPanel?: boolean;
}

export default function CourseSetSelector({
  courseSetId, locationCode, siteId, onChange, disabled, variant, refresh, className, inPanel
}: CourseSetSelectorProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [courseSets, setCourseSets] = useState<CourseSetDTO[]>([]);
  const [selectedValue, setSelectedValue] = useState('');
  const [initialCourseSet, setInitialCourseSet] = useState<CourseSetDTO | undefined>();
  const memoOnChange = useCallback(onChange, []);

  const fetchCourses = useCallback(async () => {
    setSelectedValue('');
    setCourseSets([]);

    const data = await courseService.getCourseSets(locationCode, siteId);
    if (data && data.courseSets) {
      setCourseSets(data.courseSets);
      if(data.courseSets.length === 1) {
        memoOnChange(data.courseSets[0]);
      }
    }
  }, [locationCode, siteId, memoOnChange ]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (refresh) {
        fetchCourses();
    }
  }, [refresh, fetchCourses]);

  useEffect(() => {
    if (courseSetId && !initialCourseSet && courseSetId !== selectedValue) {
      const selected = courseSets.find((c) => c.id === courseSetId);
      setInitialCourseSet(selected);
      memoOnChange(selected);
    }
  }, [courseSetId, selectedValue, initialCourseSet, courseSets, memoOnChange]);

  useEffect(() => {
    if (courseSets.length && courseSetId) {
      const courseSet = courseSets.find((cs: CourseSetDTO) => cs.id === courseSetId);
      if (courseSet) {
        setSelectedValue(courseSetId)
        memoOnChange(courseSet);
      }
    }
  }, [courseSets, courseSetId, memoOnChange]);

  const handleChange = (event: any) => {
    setSelectedValue(event.target.value);
    const selected: CourseSetDTO | undefined = courseSets.find((cs: CourseSetDTO) => cs.id === event.target.value);
    onChange(selected);
  }

  return (
    <FormControl sx={inPanel ? styles.pFormControlPanel : styles.pFormControl} className={className} variant={ variant || 'standard' }>
      <InputLabel htmlFor='courseSetId'>{!courseSets.length ? "No Course Sets Found" : "Select Course Set"}</InputLabel>
      <Select
        variant={ variant || "standard" }
        value={selectedValue}
        onChange={handleChange}
        inputProps={{
          'name': 'courseSetId',
          'id': 'courseSetId',
          'data-testid': 'test-courseSetId',
          'role': 'option'
        }}
        disabled={courseSets.length <= 1 || disabled}>
        {
          courseSets.length &&
          courseSets.map((courseSet: CourseSetDTO) => (
            <MenuItem key={courseSet.id} value={courseSet.id} role='option'>
              {courseSet.name}
            </MenuItem>
          ))
        }
        {
          (locationCode || siteId) && !courseSets.length &&
          <MenuItem key={0} value={0} role='option'>
              No Course Sets found
          </MenuItem>
        }
      </Select>
    </FormControl>
  );
}
